import { Controller } from '@hotwired/stimulus';
import { Helpers } from '../../helpers.js';

const BLANK = '—';

export default class extends Controller {
  static values = {
    covered: String,
    shortfall: String,
    invalidMultiple: String,
    failure: String,
  };

  static targets = [
    'form',
    'selector',
    'amountInput',
    'feesInput',
    'amountInputErrorMessage',
    'button',
    'selectedInvestor',
    'subscriptionAmount',
    'feesAmount',
    'displayAmount',
    'displayFees',
    'displayTotal',
    'displayCommission',
    'modalAmount',
    'modalTotal',
    'modalCommission',
    'modalWallet',
    'modalRemaining',
    'remainingAfter',
    'estimatedCommission',
    'walletBalance',
    'walletCoverage',
  ];

  connect () {
    this.fees = 0;
    this.readInvestor();
    this.refresh();
  }

  preventEnterOnForm (event) {
    if (event.key !== 'Enter') return;

    // Let Enter activate buttons and links (keyboard accessibility)
    if (event.target.closest('button, a, [role="button"]')) return;

    event.preventDefault();
  }

  handleSelectorChange () {
    this.readInvestor();
    this.refresh();
  }

  handleSubscriptionChange (event) {
    const amount = parseInt(event.target.value, 10);
    const shareValue = event.target.dataset.projectShareValue;

    if (!isNaN(amount) && !Helpers.numberIsMultipleOf(amount, shareValue)) {
      const [previous, next] = Helpers.closestMultiplesOf(amount, shareValue);
      this.#showAmountError(
        this.invalidMultipleValue
          .replace('%{previous}', previous)
          .replace('%{next}', next)
      );
      this.refresh();
      return;
    }

    this.#clearAmountError();
    this.fetchFees(event.target.dataset.feesUrl, amount, event.target.dataset.projectId);
    this.refresh();
  }

  // Read from the native <select> rather than from TomSelect: on connect the
  // widget may not have taken over the element yet, which used to leave the
  // confirmation modal reading "for the user ." until the selector was touched.
  readInvestor () {
    if (!this.hasSelectorTarget) return;

    const option = this.selectorTarget.selectedOptions[0];

    this.balance = option && option.dataset.balance
      ? parseFloat(option.dataset.balance)
      : null;

    this.#write(this.hasSelectedInvestorTarget && this.selectedInvestorTarget,
      option ? option.text : '');

    if (this.hasWalletBalanceTarget) {
      this.walletBalanceTarget.textContent =
        this.balance === null ? BLANK : Helpers.numberToCurrency(this.balance, 2);
    }
  }

  refresh () {
    const amount = this.#amount();
    const total = amount === null ? null : amount + this.fees;
    const commission = amount === null ? null : (amount * this.#rate()) / 100;

    this.#money(this.hasDisplayAmountTarget && this.displayAmountTarget, amount);
    this.#money(this.hasModalAmountTarget && this.modalAmountTarget, amount);
    this.#money(this.hasDisplayFeesTarget && this.displayFeesTarget,
      amount === null ? null : this.fees);
    this.#money(this.hasFeesAmountTarget && this.feesAmountTarget,
      amount === null ? null : this.fees);
    this.#money(this.hasDisplayTotalTarget && this.displayTotalTarget, total);
    this.#money(this.hasModalTotalTarget && this.modalTotalTarget, total);
    this.#money(this.hasDisplayCommissionTarget && this.displayCommissionTarget, commission);
    this.#money(this.hasModalCommissionTarget && this.modalCommissionTarget, commission);
    this.#money(this.hasEstimatedCommissionTarget && this.estimatedCommissionTarget, commission);
    this.#money(this.hasSubscriptionAmountTarget && this.subscriptionAmountTarget, amount, 0);

    this.#updateRemaining(amount);
    this.#updateCoverage(total);
    this.#updateSubscribeButton(amount);
  }

  fetchFees (feesUrl, amount, projectId) {
    if (isNaN(amount) || !feesUrl) {
      this.fees = 0;
      return;
    }

    fetch(`${feesUrl}?amount=${amount}&project_id=${projectId}`, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        this.fees = parseFloat(data.fees_amount) || 0;
        if (this.hasFeesInputTarget) this.feesInputTarget.value = this.fees;
        this.refresh();
      })
      .catch(() => {
        this.#showAmountError(this.failureValue);
      });
  }

  #amount () {
    if (!this.hasAmountInputTarget) return null;

    const amount = parseInt(this.amountInputTarget.value, 10);

    return isNaN(amount) ? null : amount;
  }

  #rate () {
    if (!this.hasEstimatedCommissionTarget) return 0;

    return parseFloat(this.estimatedCommissionTarget.dataset.rate) || 0;
  }

  #remaining () {
    if (!this.hasRemainingAfterTarget) return 0;

    return parseInt(this.remainingAfterTarget.dataset.remaining, 10) || 0;
  }

  #write (target, text) {
    if (!target) return;

    target.textContent = text;
  }

  #money (target, value, precision = 2) {
    if (!target) return;

    target.textContent = value === null ? BLANK : Helpers.numberToCurrency(value, precision);
  }

  #updateRemaining (amount) {
    const exceeded = (amount || 0) > this.#remaining();
    const left = Math.max(this.#remaining() - (amount || 0), 0);
    const formatted = Helpers.numberToCurrency(left, 0);

    this.#write(this.hasRemainingAfterTarget && this.remainingAfterTarget, formatted);
    this.#write(this.hasModalRemainingTarget && this.modalRemainingTarget, formatted);

    if (!this.hasRemainingAfterTarget) return;

    this.remainingAfterTarget.classList.toggle('text-danger', exceeded);
    this.remainingAfterTarget.classList.toggle('text-primary', !exceeded);
  }

  #updateCoverage (total) {
    if (!this.hasWalletCoverageTarget) return;

    const target = this.walletCoverageTarget;

    if (total === null || this.balance === null) {
      target.classList.add('d-none');
      this.#write(this.hasModalWalletTarget && this.modalWalletTarget, '');
      return;
    }

    const missing = total - this.balance;
    const covered = missing <= 0;
    const sentence = covered
      ? this.coveredValue
      : this.shortfallValue.replace('%{amount}', Helpers.numberToCurrency(missing, 2));

    target.classList.remove('d-none');
    target.classList.toggle('text-success-600', covered);
    target.classList.toggle('text-warning-600', !covered);
    target.textContent = sentence;

    this.#write(this.hasModalWalletTarget && this.modalWalletTarget, sentence);
  }

  #updateSubscribeButton (amount) {
    if (!this.hasButtonTarget || !this.hasSelectorTarget) return;

    const shareValue = this.hasAmountInputTarget
      ? this.amountInputTarget.dataset.projectShareValue
      : null;
    const withinBounds = amount !== null && amount > 0 && amount <= this.#remaining();
    const validMultiple = amount !== null && Helpers.numberIsMultipleOf(amount, shareValue);

    this.buttonTarget.disabled = !(this.selectorTarget.value && withinBounds && validMultiple);
  }

  #showAmountError (message) {
    if (!this.hasAmountInputErrorMessageTarget) return;

    this.amountInputErrorMessageTarget.classList.remove('d-none');
    this.amountInputErrorMessageTarget.textContent = message;
  }

  #clearAmountError () {
    if (!this.hasAmountInputErrorMessageTarget) return;

    this.amountInputErrorMessageTarget.classList.add('d-none');
    this.amountInputErrorMessageTarget.textContent = '';
  }
}
